<script setup lang="ts">
/*
 * Mirrors the prototype's .av / .av.sm / .av.lg — round monogram avatar.
 * Initials are auto-derived from `name`; color is hashed deterministically
 * across the brand palette so the same person stays the same color.
 */
import { computed } from 'vue';

type Size = 'sm' | 'md' | 'lg';

const props = withDefaults(
  defineProps<{
    name?: string;
    size?: Size;
    src?: string;
  }>(),
  { size: 'md' },
);

const palette = [
  'bg-primary',
  'bg-emerald-600',
  'bg-amber-600',
  'bg-rose-600',
  'bg-sky-600',
  'bg-violet-600',
  'bg-teal-600',
  'bg-orange-600',
];

function hash(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}

const initials = computed(() => {
  const n = props.name?.trim();
  if (!n) return '?';
  const parts = n.split(/\s+/).slice(0, 2);
  return parts
    .map((p) => p[0])
    .filter((c): c is string => Boolean(c))
    .join('')
    .toUpperCase();
});

const bgClass = computed(() => {
  if (props.src) return '';
  if (!props.name) return 'bg-slate-400';
  const idx = hash(props.name) % palette.length;
  return palette[idx] ?? 'bg-slate-400';
});

const sizing = computed(
  () =>
    ({
      sm: 'h-7 w-7 text-xs',
      md: 'h-8 w-8 text-sm',
      lg: 'h-16 w-16 text-[22px] font-bold',
    })[props.size],
);
</script>

<template>
  <span
    :class="[
      'inline-flex shrink-0 items-center justify-center rounded-full font-semibold text-white overflow-hidden',
      sizing,
      bgClass,
    ]"
    :aria-label="name"
  >
    <img v-if="src" :src="src" :alt="name" class="h-full w-full object-cover" />
    <template v-else>{{ initials }}</template>
  </span>
</template>
